import React from 'react'
import Highcharts from 'highcharts'
import { PIE_CHART_COLORS, PIE_COLORS, OTHER_LABEL } from './ChartConstants.js'

class ChartUtility {
  constructor(options) {
    this.name = ''
    this.pie = {}
    this.data = {}

    if (!options) {
      return
    }

    if (options.details) {
      this.name = options.details
    }

    if (options.pie) {
      this.pie = {
        nameField: options.pie.nameField,
        valueField: options.pie.valueField,
        value2Field: options.pie.value2Field,
      }
    }

    if (options.data) {
      this.data = this._populateData(options.data, this.name, this.pie)
    }
  }

  _getRowHeader(selector, suffix) {
    const div = document.querySelector(selector)

    if (!div) {
      return
    }

    let table = div.querySelector(`${selector}-${suffix}`) // check for top holdings and similar tables.

    if (!table) {
      // check for other tables.
      const tableSelector =
        suffix === 'table' ? '.js-data-table' : '.js-pie-table'
      table = div.querySelector(tableSelector)
    }

    if (table) {
      const row = table.querySelector('.Table-head').querySelector('.Table-row')

      if (row) {
        return row.querySelector('.Table-header')
      }
    }

    return null
  }

  _getEndSentenceFragment(dataArray, fieldName) {
    // returns "abc, def and ghi.", "abc and def.", or "abc."

    if (dataArray.length > 2) {
      return (
        <span>
          <strong>{dataArray[0][fieldName]}</strong>,{' '}
          <strong>{dataArray[1][fieldName]}</strong> and{' '}
          <strong>{dataArray[2][fieldName]}</strong>.
        </span>
      )
    } else if (dataArray.length === 2) {
      return (
        <span>
          <strong>{dataArray[0][fieldName]}</strong> and{' '}
          <strong>{dataArray[1][fieldName]}</strong>.
        </span>
      )
    } else if (dataArray.length === 1) {
      return (
        <span>
          <strong>{dataArray[0][fieldName]}</strong>.
        </span>
      )
    } else {
      return <span></span>
    }
  }

  _equalizePieDataTo100(chartData, pieNameField, pieValueField) {
    // Account for the fact they send pie data that doesn't add up to 100%. #mathisfun
    let total = 0

    for (let i = 0; i < chartData.length; i++) {
      let detail = chartData[i]
      total += Number(detail[pieValueField])
    }

    if (total < 99) {
      let otherValue = Number(100 - total).toFixed(2)
      let otherNode = {}
      otherNode[pieNameField] = OTHER_LABEL
      otherNode[pieValueField] = otherValue

      chartData.push(otherNode)
    }
  }

  _colorizePieChart(chartData, pieNameField, pieValueField) {
    const pieData = []
    const originalData = []
    const colors = []
    const hasChartData = chartData && chartData.length > 0

    if (!hasChartData) {
      return {
        pieData: [],
        originalData: [],
        mappedColors: Object.assign([], PIE_CHART_COLORS),
      }
    }

    this._equalizePieDataTo100(chartData, pieNameField, pieValueField)

    // create the pie chart array data and save original data.
    // generate the color palette for each data item, since last color could match OTHER.
    chartData.forEach((detail, i) => {
      // if we run out of colors, repeat the cycle!
      const colorIndex = i % PIE_CHART_COLORS.length

      originalData.push({
        name: detail[pieNameField],
        y: Number(detail[pieValueField]),
      })
      pieData.push({
        name: detail[pieNameField],
        y: Math.abs(Number(detail[pieValueField])),
      })
      colors.push(PIE_CHART_COLORS[colorIndex])
    })

    // if necessary, update the last color item to match OTHER.
    const lastIndex = chartData.length - 1
    const lastItem = chartData[lastIndex]

    if (lastItem[pieNameField] === OTHER_LABEL) {
      colors[lastIndex] = PIE_COLORS.PIE_COLOR_OTHER
    }

    return {
      pieData,
      originalData,
      mappedColors: colors,
    }
  }

  _populateData(data, detailName, pie) {
    const clonedData = Object.assign({}, data)
    const result = this._colorizePieChart(
      clonedData[detailName],
      pie.nameField,
      pie.valueField
    )

    return {
      tableData: clonedData,
      pieData: result.pieData,
      pieHeaderData: {
        nameField: pie.nameField,
        valueField: pie.valueField,
        value2Field: pie.value2Field,
      },
      originalData: result.originalData,
      colors: result.mappedColors,
    }
  }

  _renderData(chartId, defaultColors, originalData, data) {
    const self = this

    Highcharts.chart(chartId, {
      chart: {
        height: 200,
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        style: {
          fontFamily: 'Franklin Gothic Condensed',
          fontSize: '14px',
        },
        type: 'pie',
        width: 200,
      },
      colors: defaultColors,
      credits: false,
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: false,
          },
        },
        series: {
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      title: {
        text: null,
      },
      tooltip: {
        backgroundColor: PIE_COLORS.PIE_BG_COLOR,
        borderColor: PIE_COLORS.PIE_BORDER_COLOR,
        borderRadius: 5,
        padding: 10,
        formatter: function () {
          let html = `<h4 class="u-marginBottom1gu">${this.point.name}</h4>`

          if (originalData) {
            html += `<p class="u-textSecondary">${self._lookupOriginalPieValueFromKey(
              originalData,
              this.point.name
            )}%</p>`
          } else {
            html += `<p class="u-textSecondary">${this.point.y}%</p>`
          }

          return html
        },
        shadow: false,
        useHTML: true,
      },
      series: [
        {
          name: 'lorem ipsum',
          colorByPoint: true,
          data: data,
        },
      ],
    })
  }

  _equalizeTableHeadings(topLevelSelector) {
    const dataHeader = this._getRowHeader(`#${topLevelSelector}`, 'table')
    const pieHeader = this._getRowHeader(`#${topLevelSelector}`, 'body')

    if (
      dataHeader &&
      pieHeader &&
      dataHeader.offsetHeight > pieHeader.offsetHeight
    ) {
      // Due to a rendering issue (we think is related to race condition with the fonts loading),
      // the table header heights mismatch, so make them level again. #cssisfun
      pieHeader.style.height = dataHeader.offsetHeight + 'px'
      dataHeader.style.height = pieHeader.offsetHeight + 'px'
    }
  }

  _lookupOriginalPieValueFromKey(list, key) {
    let value = ''

    list.forEach((item) => {
      if (item.name === key) {
        value = item.y // allow negative values. #piecharts
      }
    })

    return value
  }

  getPieChartDescription(sentencePredicate) {
    const holdings = this.getHoldings()
    const pieNameField = this.data.pieHeaderData.nameField

    if (sentencePredicate) {
      return (
        <span>
          {sentencePredicate}{' '}
          {this._getEndSentenceFragment(holdings, pieNameField)}
        </span>
      )
    }

    return <span></span>
  }

  getPieHeaderData() {
    return this.data.pieHeaderData
  }

  getOriginalData() {
    return this.data.originalData
  }

  getHoldings() {
    return this.data.tableData[this.name]
  }

  normalizeTableData() {
    const pieHeaderData = this.getPieHeaderData()
    const pieNameField = pieHeaderData.nameField
    const valueField = pieHeaderData.valueField
    const value2Field = pieHeaderData.value2Field
    const holdings = this.getHoldings()

    return holdings.map((holding, i) => {
      let holdingDetail = {
        holdingName: holding[pieNameField],
        holdingValue: holding[valueField],
      }

      if (value2Field) {
        holdingDetail.holdingValue2 = holding[value2Field]
      }

      // if we run out of colors, lets repeat them!
      const colorIndex = i % PIE_CHART_COLORS.length

      if (holding[pieNameField] === OTHER_LABEL) {
        holdingDetail.color = PIE_COLORS.PIE_COLOR_OTHER
      } else {
        holdingDetail.color = PIE_CHART_COLORS[colorIndex]
      }

      return holdingDetail
    })
  }

  /**
   * @param id - top level id
   * @param data - array of data in the format of [{name: 'name', y: 123}]
   * @param options - additional data for rendering the chart.
   */
  renderPieChart(id, data, options) {
    let defaultColors = PIE_CHART_COLORS
    let originalData = this.getOriginalData()
    let chartId = `${id}-pie-chart`

    data = data || this.data.pieData

    if (this.data.colors) {
      defaultColors = this.data.colors
    }

    if (options) {
      if (options.colors) {
        defaultColors = options.colors
      }

      if (options.originalData) {
        originalData = options.originalData
      }
    }

    if (data && data.length > 0) {
      this._renderData(chartId, defaultColors, originalData, data)

      // Note: do not use jquery here.
      const chart = document.querySelector(`#${chartId} > div`)
      if (chart) {
        chart.style.margin = '0 auto'
      }
    }

    this._equalizeTableHeadings(id)
  }
}

export default ChartUtility
